import React from "react";

import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";

import { UserAnalyticsService } from "@analytics/index";

import EmailCell from "@components/ContactTable/EmailCell";
import RowFlag from "@components/Flagging/RowFlag";
import RowActionsMenu from "@components/RowActionsMenu";
import ColumnHeader from "@components/Table/ColumnHeader";

import { ContactType } from "@interfaces/contact.type";

import getConferenceAttendance from "@store/utilities/ConferenceAttendance";

interface IContactsTableConfig {
  fetchEmailForContact: (contactId: string) => Promise<void>;
  fetchingEmails: string[];
  role: string;
  rowMenuItems?: { func: (contactId: string) => void; label: string }[];
  workflow: string;
}

const generateContactsTableColumns = ({
  fetchEmailForContact,
  fetchingEmails,
  role,
  rowMenuItems,
  workflow,
}: IContactsTableConfig) => {
  const contactScore = {
    field: "score",
    renderCell: (parameters: GridRenderCellParams<ContactType>) => {
      return (
        <Box alignItems="center" display="flex">
          <Chip
            color={parameters.row.score > 70 ? "primary" : "warning"}
            label={parameters.row.score}
            size="small"
            sx={{
              color: "white",
              fontWeight: 600,
              px: 0.5,
            }}
          />
        </Box>
      );
    },
    renderHeader: () => <ColumnHeader title="Contact Rank" tooltip="" />,
    width: 225,
  };

  const columns = [
    {
      field: "flag",
      renderCell: (parameters: GridRenderCellParams<ContactType>) => (
        <RowFlag form="contact" rowParams={parameters} workflow={workflow} />
      ),
      renderHeader: () => <ColumnHeader title="" tooltip="" />,
      sortable: false,
      width: 50,
    },
    {
      description: "The logo of company a contact works at",
      field: "companyLogo",
      renderCell: (parameters: GridRenderCellParams<ContactType>) => {
        return (
          <Box
            alt={`${parameters.row.companyName} logo`}
            component="img"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
            src={parameters.row.companyLogoUrl ?? ""}
            sx={{ maxHeight: "51px", maxWidth: "51px", width: "auto" }}
          />
        );
      },
      renderHeader: () => <ColumnHeader title="" tooltip="" />,
      width: 71,
    },
    {
      field: "name",
      renderCell: (parameters: GridRenderCellParams<ContactType>) => {
        const { attendedConferences, tooltipText } = getConferenceAttendance(parameters.row);
        return (
          <Box alignItems="center" display="flex">
            <Typography variant="subtitle2">{parameters.value}</Typography>
            {attendedConferences.length > 0 && (
              <Tooltip title={tooltipText}>
                <VerifiedRoundedIcon color="success" sx={{ cursor: "help", marginLeft: "8px" }} />
              </Tooltip>
            )}
          </Box>
        );
      },
      renderHeader: () => <ColumnHeader title="Name" tooltip="" />,
      width: 225,
    },
    ...(role === "Admin" ? [contactScore] : []),
    {
      field: "title",
      renderCell: (parameters: GridRenderCellParams<ContactType>) => (
        <Typography variant="subtitle2">{parameters.value}</Typography>
      ),
      renderHeader: () => <ColumnHeader title="Job Title" tooltip="Use the Job Title filter to narrow your search. " />,
      width: 350,
    },
    {
      field: "emailStatus",
      renderCell: (parameters: GridRenderCellParams<ContactType>) => (
        <Typography variant="subtitle2">{parameters.value}</Typography>
      ),
      renderHeader: () => (
        <ColumnHeader
          title="Email Status"
          tooltip="An attempt was made to verify this email address, but be aware that roles and emails can change."
        />
      ),
      width: 135,
    },
    {
      field: "email",
      renderCell: (parameters: GridRenderCellParams<ContactType>) => (
        <EmailCell
          email={parameters.row.email}
          emailStatus={parameters.row.emailStatus || "TODO: MAKE EMAIL NOT NULLABLE"}
          fetchEmail={() => void fetchEmailForContact(parameters.id as string)}
          isFetching={fetchingEmails.includes(parameters.id as string)}
        />
      ),
      renderHeader: () => (
        <ColumnHeader title="Email Address" tooltip="The most up to date email found for this individual." />
      ),
      width: 320,
    },
    {
      field: "linkedinUrl",
      renderCell: (parameters: GridRenderCellParams<ContactType>) => (
        <a
          href={parameters.row.linkedinUrl}
          onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
            UserAnalyticsService.handleLinkAnalytics(event, parameters.row.linkedinUrl, "personal linkedin")
          }
          rel="noreferrer nofollow"
          target="_blank"
        >
          <Typography variant="subtitle2">View LinkedIn</Typography>
        </a>
      ),
      renderHeader: () => (
        <ColumnHeader title="LinkedIn" tooltip="Check out their LinkedIn profile to gain insights when reaching out." />
      ),
      width: 120,
    },
    {
      field: "companyName",
      renderCell: (parameters: GridRenderCellParams<ContactType>) => {
        const name = parameters.row.companyName;
        return <Typography variant="subtitle2">{name}</Typography>;
      },
      renderHeader: () => <ColumnHeader title="Company Name" tooltip="" />,
      width: 300,
    },
    {
      field: "seniority",
      renderCell: (parameters: GridRenderCellParams) => <Typography variant="subtitle2">{parameters.value}</Typography>,
      renderHeader: () => (
        <ColumnHeader title="Seniority" tooltip="Find with the right decision maker by using the Keywords filter." />
      ),
      width: 120,
    },
    {
      field: "state",
      renderCell: (parameters: GridRenderCellParams) => <Typography variant="subtitle2">{parameters.value}</Typography>,
      renderHeader: () => (
        <ColumnHeader title="State" tooltip="Use the Location filter to narrow your results by State." />
      ),
      width: 175,
    },
    {
      field: "city",
      renderCell: (parameters: GridRenderCellParams) => <Typography variant="subtitle2">{parameters.value}</Typography>,
      renderHeader: () => (
        <ColumnHeader title="City" tooltip="Use the Location filter to narrow your results by City." />
      ),
      width: 135,
    },
    {
      field: "country",
      renderCell: (parameters: GridRenderCellParams) => <Typography variant="subtitle2">{parameters.value}</Typography>,
      renderHeader: () => (
        <ColumnHeader title="Country" tooltip="Use the Location filter to narrow your results by Country." />
      ),
      width: 135,
    },
  ];

  if (rowMenuItems) {
    columns.splice(0, 0, {
      description: "",
      field: "rowActions",
      renderCell: (parameters) => {
        return (
          <RowActionsMenu
            rowMenuItems={rowMenuItems.map((menuItem) => {
              return {
                func: () => menuItem.func(parameters.id.toString()),
                label: menuItem.label,
              };
            })}
          />
        );
      },
      renderHeader: () => ColumnHeader({ title: "", tooltip: "" }),
      width: 60,
    });
  }

  return columns;
};

export default generateContactsTableColumns;
